import Table from "../../components/common/Table";
import { EyeIcon } from "@heroicons/react/24/outline";
import StatusBadge from "../../components/common/StatusBadge";
import { formatDate } from "../../utils/HelperMethod";
import { useState } from "react";
import Pagination from "../../components/common/Pagination";
import StatusFilterTabs from "../../components/common/StatusFilterTab";
import SearchBar from "../../components/common/Search";
import { useNavigate } from "react-router-dom";
import HeroSection from "../../components/UserComponents/heroSection";
import { useUserBookings } from "../../custom-hooks/queries";

export default function MyBookingsPage() {
  const navigate = useNavigate();

  // State for filters and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("All");
  const [searchText, setSearchText] = useState("");
  const pageSize = 10;

  // Fetch bookings from API
  const { data, isLoading, isError } = useUserBookings({
    page: currentPage,
    pageSize,
    status: activeTab,
    search: searchText,
  });

  // Map API response to table format (handle different response structures)
  const getBookingsArray = () => {
    // Check various possible response structures
    if (Array.isArray(data?.data?.data)) return data.data.data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  };

  const bookings = getBookingsArray().map((item) => ({
    bookingId: item.bookingId,
    experienceId: item.experienceId,
    experienceName: item.experienceName,
    vendorName: item.vendorName,
    date: item.date,
    timeSlot: item.timeSlot,
    amount: item.amount,
    status: item.status,
    experienceImage: item.experienceImage,
  }));

  // Get pagination info (handle nested response structure)
  const paginationData = data?.data || data || {};
  const pagination = {
    totalRecords: paginationData.totalRecords || 0,
    totalPages: paginationData.totalPages || 1,
    currentPage: paginationData.currentPage || currentPage,
    pageSize: paginationData.pageSize || pageSize,
  };

  const bookingColumns = [
    {
      key: "experienceInfo",
      label: "EXPERIENCE",
      width: "1.8fr",
      align: "left",
      render: (row) => (
        <div className="flex items-center gap-3">
          <img
            src={row.experienceImage || "https://via.placeholder.com/60"}
            alt={row.experienceName}
            className="w-14 h-14 rounded-lg object-cover"
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/60";
            }}
          />
          <div className="font-semibold text-gray-900">
            {row.experienceName}
          </div>
        </div>
      ),
    },
    {
      key: "vendorName",
      label: "HOST",
      width: "1.2fr",
      align: "center",
      render: (row) => <span className="text-gray-700">{row.vendorName}</span>,
    },
    {
      key: "date",
      label: "DATE",
      width: "1.2fr",
      align: "center",
      render: (row) => (
        <div>
          <div className="font-medium">{formatDate(row.date)}</div>
          <div className="text-sm text-gray-500">{row.timeSlot}</div>
        </div>
      ),
    },
    {
      key: "amount",
      label: "AMOUNT",
      width: "1fr",
      align: "center",
      render: (row) => <span className="font-semibold">${row.amount}</span>,
    },
    {
      key: "status",
      label: "STATUS",
      width: "1fr",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "actions",
      label: "ACTIONS",
      width: "0.7fr",
      align: "center",
      render: (row) => (
        <button
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"
          onClick={() => navigate(`/user/booking-detail/${row.bookingId}`)}
        >
          <EyeIcon className="w-5 h-5 text-gray-700" />
        </button>
      ),
    },
  ];

  // HANDLER: Search Bar Input
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page on search
  };

  // HANDLER: Tab Filter
  const handleFilterChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // HANDLER: Page Change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="py-4 sm:py-6 px-2 sm:px-1">
      <HeroSection
        title="Your bookings"
        description="Search your bookings"
        showSearchBar={false}
      />
      <div className="mt-8 px-10">
        <div
          className="
          bg-white 
          rounded-[12px]
          border border-[#D9D9D9]
          shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]
          overflow-hidden
          mt-4 sm:mt-6"
        >
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 sm:gap-0 py-6 sm:py-10 px-4 sm:px-6">
            <SearchBar onSearch={handleSearch} />
            <StatusFilterTabs
              filters={["All", "Upcoming", "Ongoing", "Completed"]}
              active={activeTab}
              onChange={handleFilterChange}
            />
          </div>

          {isError ? (
            <div className="flex justify-center items-center py-20 text-red-500">
              You don’t have any bookings yet..
            </div>
          ) : (
            <Table
              columns={bookingColumns}
              data={bookings}
              isLoading={isLoading}
              skeletonRows={pageSize}
              emptyMessage="You don’t have any bookings yet."
            />
          )}
        </div>

        {!isLoading && !isError && pagination.totalPages > 1 && (
          <div className="mt-4 sm:mt-6">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
